import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TRANSLATIONS } from "@/i18n";
import {
  MAX_HISTORY_ITEMS,
  COPY_FEEDBACK_DURATION_MS,
  OUTPUT_SCROLL_DELAY_MS,
  LS_KEYS,
  TOP_MODES,
  REVERSE_INTENSITY_DEFAULT,
  DIPLOMACY_DEFAULT,
} from "@/constants/diplomate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const readTopMode = () => {
  const v = localStorage.getItem(LS_KEYS.topMode);
  return TOP_MODES.includes(v) ? v : "cordialement";
};

export function useCordialement() {
  const [lang, setLang] = useState(() => localStorage.getItem(LS_KEYS.lang) || "fr");
  const [topMode, setTopModeState] = useState(readTopMode);
  const [mode, setMode] = useState("reformulate"); // sub-mode for CORDIALEMENT
  const [tone, setTone] = useState("professional");
  const [diplomacy, setDiplomacy] = useState(() => {
    const stored = parseInt(localStorage.getItem(LS_KEYS.diplomacy) || "", 10);
    return Number.isFinite(stored) && stored >= 0 && stored <= 100 ? stored : DIPLOMACY_DEFAULT;
  });
  const [reverseIntensity, setReverseIntensity] = useState(
    () => localStorage.getItem(LS_KEYS.reverseIntensity) || REVERSE_INTENSITY_DEFAULT,
  );
  const [text, setText] = useState("");
  const [output, setOutput] = useState(""); // CORDIALEMENT / REVERSE text output
  const [antidoteResult, setAntidoteResult] = useState(null); // structured
  const [bullshitResult, setBullshitResult] = useState(null); // structured
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(() => readJSON(LS_KEYS.history, []));
  const [quota, setQuota] = useState(null);
  const outputRef = useRef(null);

  // Persist prefs
  useEffect(() => {
    localStorage.setItem(LS_KEYS.lang, lang);
  }, [lang]);
  useEffect(() => {
    localStorage.setItem(LS_KEYS.topMode, topMode);
  }, [topMode]);
  useEffect(() => {
    localStorage.setItem(LS_KEYS.diplomacy, String(diplomacy));
  }, [diplomacy]);
  useEffect(() => {
    localStorage.setItem(LS_KEYS.reverseIntensity, reverseIntensity);
  }, [reverseIntensity]);
  useEffect(() => {
    localStorage.setItem(
      LS_KEYS.history,
      JSON.stringify(history.slice(0, MAX_HISTORY_ITEMS)),
    );
  }, [history]);

  const refreshQuota = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quota`);
      setQuota(data);
    } catch {
      /* offline / preview limits OK */
    }
  }, []);

  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  // Switching mode clears output/results but keeps text (so users can pipe messages between modes)
  const setTopMode = useCallback((next) => {
    setTopModeState(next);
    setOutput("");
    setAntidoteResult(null);
    setBullshitResult(null);
  }, []);

  const _handleErr = useCallback(
    (err) => {
      const t = TRANSLATIONS[lang];
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || t.error;
      if (status === 429) {
        toast.error(msg);
        refreshQuota();
      } else if (status === 503) {
        toast.error(msg);
      } else if (
        status === 502 &&
        detail &&
        typeof detail === "object" &&
        detail.error === "llm_parse_error"
      ) {
        toast.error(t.parseError);
      } else {
        toast.error(t.error);
      }
    },
    [lang, refreshQuota],
  );

  const _scrollToOutput = useCallback(() => {
    setTimeout(() => {
      outputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, OUTPUT_SCROLL_DELAY_MS);
  }, []);

  // ============ Dispatch per top mode ============
  const generate = useCallback(async () => {
    const t = TRANSLATIONS[lang];
    if (!text.trim()) {
      toast.error(t.inputRequired);
      return;
    }
    setLoading(true);
    setOutput("");
    setAntidoteResult(null);
    setBullshitResult(null);
    try {
      if (topMode === "cordialement") {
        const { data } = await axios.post(`${API}/transform`, {
          mode,
          tone,
          language: lang,
          diplomacy,
          text,
        });
        setOutput(data.output_text);
        setHistory((prev) =>
          [
            {
              id: data.id,
              topMode: "cordialement",
              mode,
              tone,
              diplomacy,
              language: lang,
              input: text,
              output: data.output_text,
              at: data.created_at,
            },
            ...prev,
          ].slice(0, MAX_HISTORY_ITEMS),
        );
      } else if (topMode === "reverse") {
        const { data } = await axios.post(`${API}/reverse`, {
          intensity: reverseIntensity,
          language: lang,
          text,
        });
        setOutput(data.output_text);
        setHistory((prev) =>
          [
            {
              id: data.id,
              topMode: "reverse",
              reverseIntensity,
              language: lang,
              input: text,
              output: data.output_text,
              at: data.created_at,
            },
            ...prev,
          ].slice(0, MAX_HISTORY_ITEMS),
        );
      } else if (topMode === "antidote") {
        const { data } = await axios.post(`${API}/antidote`, {
          language: lang,
          text,
        });
        setAntidoteResult(data.result);
        setHistory((prev) =>
          [
            {
              id: data.id,
              topMode: "antidote",
              language: lang,
              input: text,
              antidote: data.result,
              at: data.created_at,
            },
            ...prev,
          ].slice(0, MAX_HISTORY_ITEMS),
        );
      } else if (topMode === "bullshit") {
        const { data } = await axios.post(`${API}/bullshit_score`, {
          language: lang,
          text,
        });
        setBullshitResult(data.result);
        setHistory((prev) =>
          [
            {
              id: data.id,
              topMode: "bullshit",
              language: lang,
              input: text,
              bullshit: data.result,
              at: data.created_at,
            },
            ...prev,
          ].slice(0, MAX_HISTORY_ITEMS),
        );
      }
      _scrollToOutput();
      refreshQuota();
    } catch (err) {
      _handleErr(err);
    } finally {
      setLoading(false);
    }
  }, [
    text,
    topMode,
    mode,
    tone,
    diplomacy,
    reverseIntensity,
    lang,
    refreshQuota,
    _scrollToOutput,
    _handleErr,
  ]);

  const copy = useCallback(
    async (payloadOverride) => {
      const payload = payloadOverride ?? output;
      if (!payload) return;
      try {
        await navigator.clipboard.writeText(payload);
        setCopied(true);
        toast.success(TRANSLATIONS[lang].copied);
        setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION_MS);
      } catch {
        // clipboard permission denied / insecure context — fall back gracefully
        toast.error(TRANSLATIONS[lang].error);
      }
    },
    [output, lang],
  );

  const loadFromHistory = useCallback((item) => {
    // Restore top-level state from the archived item; sub-fields are best-effort.
    if (item.topMode) setTopModeState(item.topMode);
    if (item.mode) setMode(item.mode);
    if (item.tone) setTone(item.tone);
    if (Number.isFinite(item.diplomacy)) setDiplomacy(item.diplomacy);
    if (item.reverseIntensity) setReverseIntensity(item.reverseIntensity);
    setText(item.input || "");
    setOutput(item.output || "");
    setAntidoteResult(item.antidote || null);
    setBullshitResult(item.bullshit || null);
  }, []);

  const toggleLang = useCallback(() => {
    setLang((c) => (c === "fr" ? "en" : "fr"));
  }, []);

  const clearHistory = useCallback(() => setHistory([]), []);

  return {
    lang,
    topMode,
    mode,
    tone,
    diplomacy,
    reverseIntensity,
    text,
    output,
    antidoteResult,
    bullshitResult,
    loading,
    copied,
    history,
    quota,
    outputRef,
    setTopMode,
    setMode,
    setTone,
    setDiplomacy,
    setReverseIntensity,
    setText,
    generate,
    copy,
    loadFromHistory,
    toggleLang,
    clearHistory,
  };
}
